'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useCartStore, CartItem } from '@/store/cart-store';
import { toast } from 'sonner';
import { Plus, Minus } from 'lucide-react';
import { cn } from '@/lib/utils'; // Add this import

export interface CartButtonProps {
  product: Omit<CartItem, 'quantity'>;
  variant?: 'default' | 'outline' | 'secondary';
  size?: 'default' | 'sm' | 'lg';
  onAddToCart?: () => void;
  className?: string;
  buttonLabel?: string;
  cartPage?: boolean;
  get_bill_breakdown?: (opts : any) => void;
}

interface CartStoreState {
  items: CartItem[];
  // Add other store state properties if needed
}

export function CartButton({ 
  product, 
  variant = 'default', 
  size = 'default', 
  onAddToCart,
  className,
  get_bill_breakdown,
  cartPage = false,
  buttonLabel = 'Add' // Now explicitly typed through interface
}: CartButtonProps) {
  const store = useCartStore();
  const [quantity, setQuantity] = useState(0);

  useEffect(() => {
    const currentItem = store?.getState()?.items?.find((item : any) => item.id === product.id);
    setQuantity(currentItem?.quantity || 0);

    const unsubscribe = store?.subscribe((state: CartStoreState) => {
      const updatedItem = state.items.find(item => item.id === product.id);
      setQuantity(updatedItem?.quantity || 0);
    });

    return () => unsubscribe?.();
  }, [store, product.id]);

  const addToCart = () => {
    if (!store) return;
    if (onAddToCart) {
      onAddToCart();
    } else {
      store.getState().addItem(product);
      if (quantity === 0) {
        toast.success(`${product.name} has been added to your cart`);
      }
    }
    // Reset promo and loyalty points when quantity changes
    get_bill_breakdown?.({
      product_id: product.id, 
      quantity: quantity + 1,
      removePromo: true,
      loyalty_points: 0
    });
  };

  const removeFromCart = () => {
    if (!store) return;
    
    const currentItem = store.getState().items.find((item: CartItem) => item.id === product.id);
    if (currentItem?.quantity === 1) {
      toast.warning("Remove item from cart?", {
        action: {
          label: "Remove",
          onClick: () => {
            store.getState().removeItem(product.id);
            // Reset promo and loyalty points when item is removed
            get_bill_breakdown?.({
              removePromo: true,
              loyalty_points: 0
            });
          }
        },
        cancel: {
          label: "Cancel",
          onClick: () => {}
        },
        duration: 5000,
      });
    } else {
      store.getState().decreaseQuantity(product.id);
      // Reset promo and loyalty points when quantity changes
      get_bill_breakdown?.({
        product_id: product.id, 
        quantity: currentItem!.quantity - 1,
        removePromo: true,
        loyalty_points: 0
      });
    }
  };

  if (quantity === 0) {
    return (
      <button 
        onClick={addToCart}
        className={cn(
          "flex items-center justify-center gap-2 px-4 py-2 bg-[#900f21]/10  rounded-full transition-all duration-300 font-medium hover:bg-[#900f21] hover:text-white z-0",
          className
        )}
      >
        {buttonLabel} {/* Use the prop here */}
      </button>
    );
  }

  return (
    <div className={cn(
      "flex items-center justify-between py-1 px-2 bg-[#900f21]/10 rounded-full",
      className
    )}>
      <button 
        onClick={removeFromCart}
        disabled={cartPage && quantity == 1 ? true : false}
        className="w-8 h-8 flex items-center justify-center text-[#900f21] hover:bg-[#900f21]/20 rounded-full"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="w-8 text-center font-medium text-[#900f21]">
        {quantity}
      </span>
      <button 
        onClick={addToCart}
        className="w-8 h-8 flex items-center justify-center text-[#900f21] hover:bg-[#900f21]/20 rounded-full"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
